import { useState, useEffect, useMemo } from 'react';
import {
    Stack,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    LinearProgress,
    Alert,
    SelectChangeEvent,
    Checkbox,
    ListItemText,
    OutlinedInput,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import MainCard from 'components/ui/Card/MainCard';
import { getAvailableIntervals } from 'utils/interval';
import { useGetAvailableMetricTypesBasedOnUserPermissionsMetaMetricsMetrictypesGetQuery } from 'store/metadata/metadataApi';
import {
    useGetApiUserPreferencesParsedQuery,
    usePutApiUserPreferencesMutation,
} from 'store/userOrg/enhancedUserOrgApi';
import MetricChart from './MetricChart';

interface MetricsExplorerProps {
    project: string | null | undefined;
}

interface MetricTypesResponse {
    metric_types: string;
}

const minDateOfTheFrom = dayjs('2022-01-01');

const MetricsExplorer = ({ project }: MetricsExplorerProps) => {
    const { data: preferences } = useGetApiUserPreferencesParsedQuery();
    const [putPreferences] = usePutApiUserPreferencesMutation();

    const [selectedMetricTypes, setSelectedMetricTypes] = useState<string[]>([]);
    const [metricChartTypes, setMetricChartTypes] = useState<Record<string, string>>({});
    const [from, setFrom] = useState<dayjs.Dayjs | null>(dayjs().subtract(30, 'day'));
    const [to, setTo] = useState<dayjs.Dayjs | null>(dayjs());
    const [timeInterval, setTimeInterval] = useState<string>(() => {
        const savedInterval = preferences?.ddiDashboardOptions?.interval;
        if (savedInterval) {
            return savedInterval;
        }
        return '1d';
    });

    const {
        data: metricTypesData,
        isLoading: metricTypesLoading,
        error: metricTypesError,
    } = useGetAvailableMetricTypesBasedOnUserPermissionsMetaMetricsMetrictypesGetQuery({
        project: project || '',
    });

    const is403Error = metricTypesError && 'status' in metricTypesError && metricTypesError.status === 403;

    const availableMetricTypes = useMemo(() => {
        if (!metricTypesData || typeof metricTypesData !== 'object' || metricTypesData === null) return [];
        if ('errorString' in metricTypesData) return [];
        if (!('metric_types' in metricTypesData)) return [];
        const types = (metricTypesData as MetricTypesResponse).metric_types;
        if (typeof types !== 'string') return [];
        return types.split(',').map(type => type.trim());
    }, [metricTypesData]);

    useEffect(() => {
        if (preferences?.ddiDashboardOptions) {
            const { interval: savedInterval } = preferences.ddiDashboardOptions;

            if (savedInterval) {
                setTimeInterval(savedInterval);
            }
        }
    }, [preferences]);

    useEffect(() => {
        if (availableMetricTypes.length > 0 && selectedMetricTypes.length === 0) {
            const savedMetrics = preferences?.ddiDashboardOptions?.selectedMetrics;
            if (savedMetrics && project) {
                const projectMetrics = savedMetrics.filter(
                    metric => metric.project === project && availableMetricTypes.includes(metric.type)
                );

                if (projectMetrics.length > 0) {
                    const metricTypes = projectMetrics.map(metric => metric.type);
                    const chartTypesMap: Record<string, string> = {};
                    projectMetrics.forEach(metric => {
                        chartTypesMap[metric.type] = metric.chartType;
                    });

                    setSelectedMetricTypes(metricTypes);
                    setMetricChartTypes(chartTypesMap);
                    return;
                }
            }

            setSelectedMetricTypes([availableMetricTypes[0]]);
        }
    }, [availableMetricTypes, selectedMetricTypes, preferences, project]);

    const savePreferencesToServer = (
        updates: Partial<{ endTime: string; interval: string; selectedMetrics: any[] }>
    ) => {
        if (preferences) {
            putPreferences({
                body: {
                    ...preferences,
                    ddiDashboardOptions: {
                        ...preferences.ddiDashboardOptions,
                        ...updates,
                    },
                },
            });
        }
    };

    const handleChartTypeChange = (metricType: string, chartType: string) => {
        setMetricChartTypes(prev => ({ ...prev, [metricType]: chartType }));

        if (project && preferences) {
            const existingMetrics = preferences.ddiDashboardOptions?.selectedMetrics || [];
            const updatedMetrics = existingMetrics.map(metric => {
                if (metric.project === project && metric.type === metricType) {
                    return { ...metric, chartType };
                }
                return metric;
            });

            savePreferencesToServer({ selectedMetrics: updatedMetrics });
        }
    };

    const handleMetricTypeChange = (event: SelectChangeEvent<typeof selectedMetricTypes>) => {
        const {
            target: { value },
        } = event;
        const newSelectedTypes = typeof value === 'string' ? value.split(',') : value;
        setSelectedMetricTypes(newSelectedTypes);

        if (project && preferences) {
            const newSelectedMetrics = newSelectedTypes.map(type => ({
                project: project,
                type: type,
                chartType: metricChartTypes[type] || 'default',
            }));

            const existingMetrics = preferences.ddiDashboardOptions?.selectedMetrics || [];
            const otherProjectMetrics = existingMetrics.filter(metric => metric.project !== project);

            const updatedSelectedMetrics = [...otherProjectMetrics, ...newSelectedMetrics];

            savePreferencesToServer({ selectedMetrics: updatedSelectedMetrics });
        }
    };

    const availableIntervals = useMemo(() => getAvailableIntervals(from, to), [from, to]);

    useEffect(() => {
        const allowedValues = availableIntervals.map(interval => interval.value);
        if (timeInterval && !allowedValues.includes(timeInterval)) {
            const newInterval = allowedValues.length > 1 ? allowedValues[1] : '';
            setTimeInterval(newInterval);
            savePreferencesToServer({ interval: newInterval });
        }
    }, [availableIntervals, timeInterval, savePreferencesToServer]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MainCard>
                <Stack spacing={3}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        useFlexGap
                        flexWrap='wrap'
                        alignItems='center'>
                        <FormControl sx={{ minWidth: 200, flex: 1 }}>
                            <InputLabel>Metric Types</InputLabel>
                            <Select
                                multiple
                                value={selectedMetricTypes}
                                onChange={handleMetricTypeChange}
                                input={<OutlinedInput label='Metric Types' />}
                                renderValue={selected =>
                                    selected
                                        .map(val => val.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
                                        .join(', ')
                                }
                                disabled={metricTypesLoading || is403Error}>
                                {availableMetricTypes.map(type => (
                                    <MenuItem key={type} value={type}>
                                        <Checkbox checked={selectedMetricTypes.indexOf(type) > -1} />
                                        <ListItemText
                                            primary={type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <DatePicker
                            label='From'
                            value={from}
                            format='YYYY-MM-DD'
                            onChange={newValue => setFrom(newValue)}
                            minDate={minDateOfTheFrom}
                            maxDate={to?.subtract(1, 'day')}
                            disabled={is403Error}
                            slotProps={{ textField: { fullWidth: true, sx: { minWidth: 200, flex: 1 } } }}
                        />
                        <DatePicker
                            label='To'
                            value={to}
                            format='YYYY-MM-DD'
                            onChange={newValue => {
                                if (newValue) {
                                    setTo(newValue);
                                }
                            }}
                            minDate={from}
                            maxDate={dayjs()}
                            disabled={is403Error}
                            slotProps={{ textField: { fullWidth: true, sx: { minWidth: 200, flex: 1 } } }}
                        />
                        <FormControl sx={{ minWidth: 200, flex: 1 }}>
                            <InputLabel>Time Interval</InputLabel>
                            <Select
                                value={timeInterval}
                                label='Time Interval'
                                disabled={is403Error}
                                onChange={e => {
                                    const newInterval = e.target.value;
                                    setTimeInterval(newInterval);
                                    savePreferencesToServer({ interval: newInterval });
                                }}>
                                {availableIntervals.map(interval => (
                                    <MenuItem key={interval.value} value={interval.value}>
                                        {interval.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    {is403Error && (
                        <Alert severity='warning'>
                            You don't have permission to view the statistics of this project. Only the project manager
                            and owner can see them.
                        </Alert>
                    )}
                    {metricTypesError && !is403Error && <Alert severity='error'>Error loading metric types.</Alert>}
                    {metricTypesLoading && <LinearProgress />}

                    <Stack direction='row' spacing={3} useFlexGap flexWrap='wrap'>
                        {selectedMetricTypes.map((metric, index) => (
                            <MetricChart
                                key={metric}
                                metricType={metric}
                                project={project || ''}
                                startTime={from?.toDate() || new Date()}
                                endTime={to?.toDate() || new Date()}
                                timeInterval={metric.includes('-table') ? '' : timeInterval}
                                chartIndex={index}
                                initialChartType={metricChartTypes[metric]}
                                onChartTypeChange={chartType => handleChartTypeChange(metric, chartType)}
                            />
                        ))}
                    </Stack>
                </Stack>
            </MainCard>
        </LocalizationProvider>
    );
};

export default MetricsExplorer;
